
import React, { useState, useEffect } from 'react';
// eslint-disable-next-line import/no-named-as-default
import ValueSelector from '../elementSelector/selectors/ValueSelector';
import { set, lensPath } from 'ramda';
import Table from '../Table';
import { dataToValueOption } from '../elementSelector/selectors/picker/valuePickerUtils';
import ValueSource from '../../sources/value';
import { contentTypesList } from '../../constants';
import { isNullOrEmpty } from '../../utils/core';
import '../../typedefs/service';
import '../../typedefs/value';
import translations from '../../translations';
import { addNotification } from '../../actions/reduxActions/notification';
import { connect } from 'react-redux';
import { SUPPORTED_SERVICES } from './ServiceConstants';

/**
 * @param configuration The configuration values from the service (no selections)
 * @param authToken The user's authentication token to perform requests
 * @param tenantId The user's tenant id to perform requests to their current tenant
 * @param doesServiceExist Indicator whether this is a service being first installed or not
 * @param currentServiceData The data about a service being edited
 * @param newServiceData The data about a newly installed service
 * @param setSelections Function to use to set the user's selections
 * @param setLoading Function to use to set the Loading state of the data
 * @param setError Function to use to save full error details
 * @param throwError Adds a notification, used specifically for errors
 * @description Fetch the latest Values in the given tenant and match the values
 * against the service configuration values. Matching the Value information from the Tenant and
 * the hard-coded service configuration information about the SUPPORTED_SERVICES
 * against the small information the service gives back to us (from "configuration"),
 * we build up a full description of each service configuration values
 * and save the full description using setSelections.
*/
export const refreshServiceConfig = (
    configuration,
    authToken, tenantId,
    doesServiceExist, currentServiceData, newServiceData,
    setSelections, setLoading, setError, throwError,
) => {
    setLoading(true);
    return ValueSource.getList(authToken, tenantId)
        .then((allTenantValues) => {
            const configValues = configuration.find(
                output => output.developerName === 'Configuration Values',
            ).objectData.map(
                (serviceValueRequest) => {
                    const name = serviceValueRequest.properties
                        .find(property => property.developerName === 'DeveloperName').contentValue;

                    const url = doesServiceExist ? currentServiceData.uri : newServiceData.url;
                    const supportedService = SUPPORTED_SERVICES.find(service => service.url === url);
                    const supportedServiceConfigValue = supportedService ?
                        supportedService.configuration ?
                            supportedService.configuration.find(cf => cf.name === name)
                            : null
                        : null;

                    const contentType = serviceValueRequest.properties
                        .find(property => property.developerName === 'ContentType').contentValue;
                    const typeElementId = serviceValueRequest.properties
                        .find(property => property.developerName === 'TypeElementId').contentValue;

                    // If the service already exists
                    /** @type {?ServiceValue} */
                    const serviceConfigValue = doesServiceExist ?
                        currentServiceData.configurationValues.find(
                            // Find the configuration value that the user chose previously (if there is one)
                            configValue => configValue.developerName === name,
                        ) :
                        null;

                    // Find the value from the Tenant to get the information about it
                    /** @type {?FullValueReference} */
                    const tenantValue = serviceConfigValue ?
                        allTenantValues.find(
                            value => value.id === serviceConfigValue.valueElementToReferenceId.id &&
                            value.typeElementPropertyId === serviceConfigValue.valueElementToReferenceId.typeElementPropertyId,
                        ) :
                        null;

                    /** @type {?ValueSelectorMetadata} */
                    const value = tenantValue ?
                        dataToValueOption(tenantValue) :
                        null;

                    /**
                     * Returns the Configuration setting information, and the selected value (if any)
                     * @returns {ConfigurationAndSelection} stores the Configuration setting information, and the selected value (if any)
                     */
                    return {
                        name,
                        contentType,
                        typeElementId,
                        value,
                        ...supportedServiceConfigValue,
                    };
                },
            );
            setLoading(false);
            setError(null);
            setSelections(configValues);
        }).catch((error) => {
            setLoading(false);
            setError(error.message);
            throwError({
                type: 'error',
                message: error.message,
                isPersistent: true,
            });
        });
};

/**
 * @param authenticationToken The user's authentication token to perform requests
 * @param tenantId The user's tenant id to perform requests to their current tenant
 * @param configuration The configuration values from the service (no selections)
 * @param newServiceData The data about a newly installed service
 * @param currentServiceData The data about a service being edited
 * @param switchView Command to switch view of ServiceController
 * @param switchToPreview Command to switch view of ServiceController specifically to Preview
 * @param installService Command to install the service ServiceController
 * @param selectedServiceView Type of service view for the new service so the user can go back
 * @param serviceType Type of service the new service is so they can go back to reselect
 * @param throwError Adds a notification, used specifically for errors
 * @description Render all of the configuration values for this service
 * and let the user select values for them using the value selector
*/
export const ServiceConfiguration = ({
    authenticationToken,
    tenantId,
    configuration,
    newServiceData,
    currentServiceData,
    switchView,
    switchToPreview,
    installService,
    selectedServiceView,
    throwError,
}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoadingTenantValues, setIsLoadingTenantValues] = useState(true);
    const [openEditor, setOpenEditor] = useState();

    /** @type {[ConfigurationAndSelection[], Function]} Get and set selected configuration values */
    const [selectedValues, setSelectedValues] = useState([]);

    const doesServiceAlreadyExist = isNullOrEmpty(currentServiceData) === false;

    // Only happens on first render, or when critical props [authenticationToken, tenantId, configuration, newServiceData, currentServiceData] change
    useEffect(
        () => {
            refreshServiceConfig(
                configuration,
                authenticationToken, tenantId,
                doesServiceAlreadyExist, currentServiceData, newServiceData,
                setSelectedValues, setIsLoadingTenantValues, setErrorMessage, throwError,
            );
        },
        [authenticationToken, tenantId, configuration, newServiceData, currentServiceData],
    );

    const toggleModal = (input, id) => input ?
        // The modal wants to open so open it
        setOpenEditor(id) :
        openEditor === id ?
            // The modal was open so close it
            setOpenEditor(null) :
            // The modal wasn't open so is already closed
            null;

    const selectValue = (meta, id) => {
        setSelectedValues(set(lensPath([id, 'value']), meta, selectedValues));
        toggleModal(false, id);
    };

    const inputs = isNullOrEmpty(errorMessage) ? (
        <Table
            className="table table-striped margin-top table-bordered"
            columns={
                [
                    {
                        renderHeader: () => 'Name',
                        renderCell: ({ item }) => item.name,
                    },
                    {
                        renderHeader: () => 'Content Type',
                        renderCell: ({ item }) => {
                            const cleanContentType = contentTypesList.find(primitiveType => primitiveType.key === item.contentType.toUpperCase());
                            const shownContentType = cleanContentType ? cleanContentType.label : item.contentType;
                            return item.typeElementId ?
                                `${shownContentType} of ${item.typeElementId}` :
                                shownContentType;
                        },
                    },
                    {
                        renderHeader: () => 'Required',
                        renderCell: ({ item }) => (
                            <div className="centre-text-horizontal">
                                <span className={`glyphicon glyphicon-${item.required ? 'ok' : 'remove'}`} />
                            </div>
                        ),
                    },
                    {
                        renderHeader: () => 'Default',
                        renderCell: ({ item }) => item.default || 'No Default',
                    },
                    {
                        renderHeader: () => 'Value',
                        renderCell: ({ item, rowIndex }) => (
                            <div className="centre-text-horizontal">
                                <ValueSelector
                                    startingValueName={`${doesServiceAlreadyExist ? currentServiceData.developerName : newServiceData.name}: `}
                                    toggleModal={toggleModal}
                                    isOpen={openEditor === rowIndex}
                                    id={rowIndex}
                                    contentTypeFilter={item.contentType}
                                    typeElementIdFilter={item.typeElementId}
                                    selectValue={selectValue}
                                    preSelectedValue={item.value}
                                />
                            </div>
                        ),
                    },
                    {
                        renderHeader: () => 'Description',
                        renderCell: ({ item }) => item.description,
                    },
                ]
            }
            items={selectedValues}
            isLoading={isLoadingTenantValues}
        />
    ) : (
        <div className="alert alert-danger">
            <span>{`${translations.SC_config_refresh_failed} "${errorMessage}".`}</span>
            <button
                type="button"
                className="btn btn-danger margin-left"
                onClick={refreshServiceConfig}
                disabled={isLoadingTenantValues}
            >
                {'Retry'}
            </button>
        </div>
    );

    return (
        <div className="full-height flex-column">
            <div className="admin-page main-scroller">
                <h1>{`Service: ${doesServiceAlreadyExist ? currentServiceData.developerName : newServiceData.name}`}</h1>
                {inputs}
            </div>
            <div className="mw-footer container-fluid">
                <button
                    type="button"
                    className="btn btn-default outcome"
                    onClick={() => switchView(selectedServiceView)}
                >
                    {'Back'}
                </button>
                <button
                    type="button"
                    className="btn btn-primary outcome"
                    onClick={() => installService(selectedValues)}
                    disabled={isLoadingTenantValues || isNullOrEmpty(errorMessage) === false}
                >
                    {'Install'}
                </button>
                <button
                    type="button"
                    className="btn btn-success outcome"
                    onClick={() => switchToPreview(selectedValues)}
                >
                    {'Preview Actions & Types'}
                </button>
            </div>
        </div>
    );
};

const mapDispatchToProps = {
    throwError: addNotification,
};

export default connect(
    null,
    mapDispatchToProps,
)(ServiceConfiguration);
